import { Component, EventEmitter, OnInit, Input, Output } from '@angular/core';
import { PageInfo } from '../restSearch/restSearch.service';
import { Model } from '@socrates/common';

@Component ({
	selector: 'pagination',
	template: `<div class="mt-2 flex w-full items-center" [hidden]="pageInfo.totalResults === 0">
		<div class="w-2/5 text-sm text-neutral-600 dark:text-neutral-400">
			<span id="om-results-metrics" [i18n] eikey="pagination.pageDesc" [args]="{'currHit':pageInfo.currentHit,'toHit':pageInfo.toHit,'totalHits':pageInfo.totalResults}" [count]="pageInfo.totalResults"></span>
		</div>
		<div class="flex-1">
			@if (pageInfo.totalPages > 1) {
				<nav>
					<ul class="flex items-center gap-0.5">
						<li id="om-nav-prev-page">
							<a (click)="navigatePage(pageInfo.currentPage - 1)"
								class="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
								[class.opacity-40]="pageInfo.pageIndex === 0"
								[class.pointer-events-none]="pageInfo.pageIndex === 0">&lt;</a>
						</li>
						<li id="om-nav-first-page">
							<a (click)="navigatePage(1)"
								class="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-sm"
								[class.bg-neutral-900]="1 === pageInfo.currentPage"
								[class.text-white]="1 === pageInfo.currentPage"
								[class.dark:bg-neutral-100]="1 === pageInfo.currentPage"
								[class.dark:text-neutral-900]="1 === pageInfo.currentPage"
								[class.text-neutral-600]="1 !== pageInfo.currentPage"
								[class.hover:bg-neutral-100]="1 !== pageInfo.currentPage"
								[class.dark:text-neutral-400]="1 !== pageInfo.currentPage"
								[class.dark:hover:bg-neutral-800]="1 !== pageInfo.currentPage">1</a>
						</li>
						@for (page of pageIndexes(); track page) {
							<li>
								<a id="om-nav-page-{{ page }}" (click)="navigatePage(page)"
									class="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-sm"
									[class.bg-neutral-900]="page === pageInfo.currentPage"
									[class.text-white]="page === pageInfo.currentPage"
									[class.dark:bg-neutral-100]="page === pageInfo.currentPage"
									[class.dark:text-neutral-900]="page === pageInfo.currentPage"
									[class.text-neutral-600]="page !== pageInfo.currentPage"
									[class.hover:bg-neutral-100]="page !== pageInfo.currentPage"
									[class.dark:text-neutral-400]="page !== pageInfo.currentPage"
									[class.dark:hover:bg-neutral-800]="page !== pageInfo.currentPage">{{ page }}</a>
							</li>
						}
						<li id="om-nav-next-page">
							<a (click)="navigatePage(pageInfo.currentPage + 1)"
								class="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
								[class.opacity-40]="pageInfo.currentPage === pageInfo.totalPages"
								[class.pointer-events-none]="pageInfo.currentPage === pageInfo.totalPages">&gt;</a>
						</li>
					</ul>
				</nav>
			}
		</div>
		@if (pageInfo.totalResults > 10) {
			<div class="ml-auto flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
				<span ro-i18n eikey="pagination.rowsDisplayed">Rows displayed</span>
				<select
					id="om-display-list"
					class="rounded border border-neutral-300 bg-white px-2 py-1 text-sm text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
					name="rowSelect"
					(change)="changeDisplayCount($event)"
					[(ngModel)]="pageOption"
				>
					@for (pageSize of pageSizeOptions; track pageSize.id) {
						<option [value]="pageSize.id" [selected]="pageSize.id === pageInfo.pageSize">{{ pageSize.label }}</option>
					}
				</select>
			</div>
		}
	</div>`,
	styleUrls: [
		'./pagination.css'
	],
	standalone: false,
})
export class PaginationComponent implements OnInit {

	@Input('page-info') pageInfo: PageInfo<Model> = new PageInfo<Model>();
	@Output('change-display-size') changeDisplaySize: EventEmitter<any> = new EventEmitter<any>();
	@Output() navigate: EventEmitter<any> = new EventEmitter<any>();


	private _pageSizeOptions: SelectOption[] = [
		new SelectOption(10, '10'),
		new SelectOption(25, '25'),
		new SelectOption(50, '50'),
		new SelectOption(100, '100')
	];
	private _pageOption: SelectOption;
	private startIndex: number;
	private endIndex: number;

	ngOnInit(): void {
		this.setPageSizeOption();
	}

	setPageSizeOption(): void {
		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let idx = 0; idx < this.pageSizeOptions.length; idx++) {
			const option = this.pageSizeOptions[idx];

			if (option.id === this.pageInfo.pageSize) {
				this._pageOption = option;
				break;
			}
		}
	}

	changeDisplayCount(evt): void {
		const newPageCount: number = +evt.currentTarget.selectedOptions[0].value;
		this.changeDisplaySize.emit({ name: 'displayCount', displaySize: newPageCount });
	}

	navigatePage(pageNumber: number): void {
		if (pageNumber > 0 && pageNumber <= this.pageInfo.totalPages && pageNumber !== this.pageInfo.currentPage) {
			this.navigate.emit({ name: 'navigate', pageNumber: pageNumber });
		}
	}

	navigateLast(): void {
		this.navigate.emit({ name: 'navigate', pageNumber: this.pageInfo.totalPages });
	}

	atFirstPage(): boolean {
		return this.pageInfo.currentPage === 1;
	}

	atEndPage(): boolean {
		return this.pageInfo.currentPage !== this.pageInfo.totalPages;
	}

	pageIndexes(): number[] {
		const indexes: number[] = [];
		if (this.pageInfo && this.pageInfo.totalPages > 1 && this.pageInfo.totalPages !== Infinity) {

			this.startIndex = this.pageInfo.currentPage - 2;
			if (this.pageInfo.totalPages > 3 && this.startIndex > this.pageInfo.totalPages - 4) {
				this.startIndex = this.pageInfo.totalPages - 4;
			}

			if (this.startIndex <= 1) {
				// 1 is always displayed
				this.startIndex = 2;
			}

			const hasTenMore: boolean = this.pageInfo.totalPages > this.pageInfo.currentPage + 10;

			this.endIndex = this.startIndex + 3;
			if (this.endIndex > this.pageInfo.totalPages) {
				// endIndex is always displayed
				this.endIndex = this.pageInfo.totalPages;
			}

			if (hasTenMore) {
				this.endIndex--;
			}


			for (let li: number = this.startIndex; li <= this.endIndex; li++) {
				indexes.push(li);
			}

			if (hasTenMore) {
				indexes.push(this.pageInfo.currentPage + 9);
			}
			if (this.pageInfo.totalPages > this.endIndex) {
				indexes.push(this.pageInfo.totalPages);
			}
		}
		return indexes;
	}

	get pageOption(): SelectOption {
		return this._pageOption;
	}

	set pageOption(newPageOption: SelectOption) {
		this._pageOption = newPageOption;
	}

	get pageSizeOptions(): SelectOption[] {
		return this._pageSizeOptions;
	}

	set pageSizeOptions(newPageSizeOptions: SelectOption[]) {
		this._pageSizeOptions = newPageSizeOptions;
	}
}

class SelectOption {

	constructor(private identifier: number, private displayLabel: string) {
	}

	get id(): number {
		return this.identifier;
	}

	get label(): string {
		return this.displayLabel;
	}
}
